/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react'
import { Button, Card, Form, Spinner, Table } from 'react-bootstrap'
import moment from 'moment'
import Select from 'react-select'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDebounce } from 'use-debounce'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import { downloadBlob } from 'helpers/generalHelper'
import {
  useExportWriteOffReportMutation,
  useGetWriteOffReportQuery,
  WriteOffReportQuery,
} from 'store/services/managementReports'
import { useGetLabPayersQuery } from 'store/services/payer'
import { WriteOffReportItem } from 'ts-back-end/src/reporting/types/write-off-report'

type PayerOption = { label: string; value: string; matchedName?: string | null }

type Drill = { category: string; categoryLabel: string; reason?: string | null; reasonLabel?: string }

const PAGE_SIZE_DEFAULT = 20

// Reversals are negative: -$10.00, not $-10.00.
const formatMoney = (value?: number | null) => {
  const amount = Number(value || 0)
  const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return `${amount < 0 ? '-' : ''}$${formatted}`
}

const formatPercent = (value: number, total: number) => (total ? `${((value / total) * 100).toFixed(1)}%` : '—')

const clickableRow = { cursor: 'pointer', '&:hover': { backgroundColor: '#f1f5f9' } }

export const WriteOffReport = () => {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [selectedPayer, setSelectedPayer] = useState<PayerOption | null>(null)
  const [payerSearch, setPayerSearch] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<Omit<WriteOffReportQuery, 'page' | 'size'>>({})
  const [drill, setDrill] = useState<Drill | null>(null)
  const [expanded, setExpanded] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)

  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)

  // A reason narrows further than its category; a write-off with no reason has only the category.
  const drillFilter = useMemo(() => {
    if (!drill) {
      return {}
    }

    return drill.reason ? { reason: drill.reason } : { category: drill.category }
  }, [drill])

  const { data, isFetching } = useGetWriteOffReportQuery({
    page: currentPage,
    size: pageSize,
    ...appliedFilters,
    ...drillFilter,
  })
  const [exportWriteOffReport, { isLoading: isExporting }] = useExportWriteOffReportMutation()

  const { data: payerList, isFetching: payersLoading } = useGetLabPayersQuery(
    debouncedPayerSearch.trim().length >= 2 ? { active: true, name: debouncedPayerSearch.trim() } : skipToken,
  )

  const payerOptions = useMemo(() => {
    const options: PayerOption[] = (payerList || []).map((payer) => ({
      value: String(payer.payerId),
      label: payer.name,
      matchedName: payer.matchedName,
    }))

    if (selectedPayer && !options.some((option) => option.value === selectedPayer.value)) {
      return [selectedPayer, ...options]
    }

    return options
  }, [payerList, selectedPayer])

  const selectDrill = useCallback((next: Drill | null) => {
    setCurrentPage(0)
    setDrill(next)
  }, [])

  const handleSearch = useCallback(() => {
    setCurrentPage(0)
    setAppliedFilters({
      from: from || undefined,
      to: to || undefined,
      payerId: selectedPayer ? Number(selectedPayer.value) : undefined,
    })
  }, [from, to, selectedPayer])

  const handleClear = useCallback(() => {
    setFrom('')
    setTo('')
    setSelectedPayer(null)
    setPayerSearch('')
    setDrill(null)
    setCurrentPage(0)
    setAppliedFilters({})
  }, [])

  const handleExportCsv = useCallback(async () => {
    const blob = await exportWriteOffReport({
      ...appliedFilters,
      ...drillFilter,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).unwrap()
    downloadBlob(blob, `write-off-report-${moment().format('YYYY-MM-DD')}.csv`)
  }, [appliedFilters, drillFilter, exportWriteOffReport])

  const toggleExpanded = (category: string) =>
    setExpanded((current) =>
      current.includes(category) ? current.filter((item) => item !== category) : [...current, category],
    )

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Posted',
        accessor: 'postedAt',
        className: 'text-nowrap',
        Cell: ({ value }: { value: string }) => moment(value).format('MM/DD/YYYY'),
      },
      {
        Header: 'Patient',
        accessor: 'patientLastName',
        Cell: ({ row }: { row: { original: WriteOffReportItem } }) => {
          const { patientId, patientFirstName, patientLastName } = row.original
          const name = [patientLastName, patientFirstName].filter(Boolean).join(', ')

          return patientId ? `#${patientId} ${name}` : name || '—'
        },
      },
      { Header: 'Payer', accessor: 'payerName', Cell: ({ value }: { value?: string | null }) => value || '—' },
      { Header: 'CPT', accessor: 'cptCode', Cell: ({ value }: { value?: string | null }) => value || '—' },
      {
        Header: 'DOS',
        accessor: 'dosStart',
        className: 'text-nowrap',
        Cell: ({ value }: { value?: string | null }) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
      },
      { Header: 'Category', accessor: 'categoryLabel' },
      { Header: 'Reason', accessor: 'reasonLabel' },
      {
        Header: 'Amount',
        accessor: 'amount',
        className: 'text-right',
        Cell: ({ value }: { value: number }) => formatMoney(value),
      },
    ],
    [],
  )

  const tableData = useMemo(
    () => ({
      items: data?.transactions?.items || [],
      currentPage: data?.transactions?.currentPage ?? currentPage,
      totalItems: data?.transactions?.totalItems ?? 0,
    }),
    [currentPage, data],
  )

  const summary = data?.summary
  const totalAmount = summary?.totalAmount ?? 0

  return (
    <div className="container-fluid">
      <Card className="mt-3">
        <Card.Header>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <h4 className="mb-0">Write-Offs</h4>
            {(isFetching || isExporting) && <Spinner animation="border" size="sm" />}
          </div>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-3 col-sm-12 mb-2">
              <label className="mb-1">Posted From</label>
              <Form.Control type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </div>
            <div className="col-md-3 col-sm-12 mb-2">
              <label className="mb-1">Posted To</label>
              <Form.Control type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </div>
            <div className="col-md-3 col-sm-12 mb-2">
              <label className="mb-1">Payer</label>
              <Select
                classNamePrefix="react-select"
                isClearable
                isLoading={payersLoading}
                value={selectedPayer}
                inputValue={payerSearch}
                onInputChange={(value) => setPayerSearch(value)}
                onChange={(option) => setSelectedPayer(option as PayerOption | null)}
                options={payerOptions}
                formatOptionLabel={formatPayerOptionLabel}
                filterOption={filterPayerOption}
                placeholder="Payer name"
                noOptionsMessage={() =>
                  payerSearch.trim().length >= 2 ? 'No payers found' : 'Type at least 2 characters'
                }
              />
            </div>
            <div className="col-md-3 col-sm-12 mb-2 d-flex align-items-end flex-wrap">
              <Button variant="primary" onClick={handleSearch} disabled={isFetching}>
                Search
              </Button>
              <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                Clear
              </Button>
              <Button
                variant="outline-success"
                className="ml-2"
                onClick={handleExportCsv}
                disabled={isExporting || !tableData.totalItems}>
                Export CSV
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header>
          <h4 className="mb-0">By Category</h4>
        </Card.Header>
        <Card.Body>
          {summary?.categories?.length ? (
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Category / Reason</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {summary.categories.map((category) => {
                  const isOpen = expanded.includes(category.category)

                  return (
                    <React.Fragment key={category.category}>
                      <tr
                        css={{
                          ...clickableRow,
                          fontWeight: 600,
                          backgroundColor:
                            drill?.category === category.category && !drill.reason ? '#e7f1ff' : undefined,
                        }}
                        onClick={() => {
                          toggleExpanded(category.category)
                          selectDrill({ category: category.category, categoryLabel: category.label })
                        }}>
                        <td>
                          <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'} mr-2`} />
                          {category.label}
                        </td>
                        <td className="text-right">{category.count}</td>
                        <td className="text-right">{formatMoney(category.amount)}</td>
                        <td className="text-right">{formatPercent(category.amount, totalAmount)}</td>
                      </tr>
                      {isOpen &&
                        category.reasons.map((reason) => (
                          <tr
                            key={`${category.category}-${reason.reason}`}
                            css={{
                              ...clickableRow,
                              backgroundColor:
                                drill?.reason && drill.reason === reason.reason ? '#e7f1ff' : undefined,
                            }}
                            onClick={() =>
                              selectDrill({
                                category: category.category,
                                categoryLabel: category.label,
                                reason: reason.reason,
                                reasonLabel: reason.label,
                              })
                            }>
                            <td css={{ paddingLeft: '2.25rem !important' }}>{reason.label}</td>
                            <td className="text-right">{reason.count}</td>
                            <td className="text-right">{formatMoney(reason.amount)}</td>
                            <td className="text-right">{formatPercent(reason.amount, totalAmount)}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  )
                })}
                <tr css={{ fontWeight: 700 }}>
                  <td>Total</td>
                  <td className="text-right">{summary.totalCount}</td>
                  <td className="text-right">{formatMoney(totalAmount)}</td>
                  <td />
                </tr>
              </tbody>
            </Table>
          ) : (
            <div className="text-muted">{isFetching ? 'Loading…' : 'No write-offs for these filters.'}</div>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <h4 className="mb-0">
              Write-Offs
              {drill && (
                <span className="text-muted ml-2" css={{ fontSize: '1rem', fontWeight: 400 }}>
                  {drill.categoryLabel}
                  {drill.reasonLabel ? ` › ${drill.reasonLabel}` : ''}
                </span>
              )}
            </h4>
            {drill && (
              <Button size="sm" variant="outline-secondary" onClick={() => selectDrill(null)}>
                Show all
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <TableWrapper className="card-body row" css={{ '& thead': { zIndex: '0 !important' } }}>
            <DataTable
              tableKey="write_off_report"
              data={tableData}
              status={isFetching}
              tableColumns={tableColumns}
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              setSortOptions={() => {}}
              searchable={false}
              entityName="write-offs"
            />
          </TableWrapper>
        </Card.Body>
      </Card>
    </div>
  )
}

export default WriteOffReport
